import React, { useState, useEffect } from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useUserStore } from '../store/userStore';
import { useNudgeStore } from '../store/nudgeStore';

const DISMISS_KEY = 'push_nudge_dismissed_at';
const DISMISS_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 7; // 7 days
const NUDGE_ID = 'push';

const wasRecentlyDismissed = () => {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  return Date.now() - parseInt(raw, 10) < DISMISS_COOLDOWN_MS;
};

/**
 * Soft, corner "enable notifications" nudge.
 *
 * Design notes (why this replaces the old full-screen modal):
 * - It only ever appears when permission is still 'default' (never asked).
 *   If permission is already 'granted', we resync in the background with
 *   NO UI at all - there is nothing for the user to approve, so there is
 *   nothing to show them.
 * - It is a small corner card, not a full-screen blocking overlay, and it
 *   always has a working close button, in every state - it can never get
 *   stuck on screen with no way out.
 * - No polling loop. It checks once when the user becomes known, and once
 *   more only if the user explicitly retries.
 * - Claims a shared "nudge slot" (nudgeStore) so it never visually stacks
 *   with the install-app banner.
 */
const PushPermissionModal = () => {
  const { currUserActive } = useUserStore();
  const { supported, permission, isSubscribed, status, subscribe } = usePushNotifications();
  const { claim, release } = useNudgeStore();

  const [showNudge, setShowNudge] = useState(false);
  const [hasTriedSilentResync, setHasTriedSilentResync] = useState(false);

  useEffect(() => {
    if (!supported || !currUserActive?.id) return;

    if (permission === 'granted') {
      // Already allowed - never interrupt the user. Just make sure our
      // record matches reality, silently, once.
      if (!hasTriedSilentResync && !isSubscribed) {
        setHasTriedSilentResync(true);
        subscribe();
      }
      return;
    }

    if (permission === 'default' && !wasRecentlyDismissed()) {
      const timer = setTimeout(() => {
        if (claim(NUDGE_ID)) setShowNudge(true);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [supported, currUserActive?.id, permission, isSubscribed, hasTriedSilentResync, subscribe, claim]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShowNudge(false);
    release(NUDGE_ID);
  };

const handleEnable = async () => {
  const result = await subscribe();
  if (result.ok) {
    toast.success('Notifications enabled!');
    dismiss();
  } else if (result.reason === 'denied' || result.reason === 'default') {
    dismiss();
  } else {
    toast.error('Could not enable notifications. Please try again.');
    // stays open so they can retry — dismiss() not called
  }
};

  if (!showNudge) return null;

  const isWorking = status === 'working';

  return (
    <div className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-4 sm:w-96 z-[9998] animate-fadeIn">
      <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-100 p-5">
        <button
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 text-lg leading-none w-6 h-6 flex items-center justify-center"
        >
          ✕
        </button>

        <h3 className="text-sm font-bold text-slate-900 pr-6 mb-1">
          Stay on top of new bookings
        </h3>
        <p className="text-xs text-slate-500 mb-4 leading-relaxed pr-2">
          Turn on notifications so you never miss a reservation. You can change this anytime in
          Settings.
        </p>

        <div className="flex gap-2">
          <button
            onClick={handleEnable}
            disabled={isWorking}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-semibold py-2.5 px-4 rounded-xl text-xs uppercase tracking-wider transition-all"
          >
            {isWorking ? 'Enabling…' : 'Enable'}
          </button>
          <button
            onClick={dismiss}
            className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold py-2.5 px-4 rounded-xl text-xs uppercase tracking-wider transition-all"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

export default PushPermissionModal;
